import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { MailStaffMember } from '@goenhub/shared';

import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import { useMailStaff, useUpdateMailStaff } from '@/hooks/useMailSettings';

export function StaffSection() {
  const { user } = useAuth();
  const canEdit = user?.role === 'owner' || user?.role === 'editor';
  const { data, isLoading } = useMailStaff();
  const updateMutation = useUpdateMailStaff();
  const [list, setList] = useState<MailStaffMember[]>([]);

  useEffect(() => {
    if (data?.list) setList(data.list);
  }, [data]);

  const addRow = () => setList([...list, { name: '', email: '' }]);
  const removeRow = (i: number) => setList(list.filter((_, idx) => idx !== i));
  const updateRow = (i: number, field: keyof MailStaffMember, value: string) => {
    setList(list.map((item, idx) => (idx === i ? { ...item, [field]: value } : item)));
  };

  const save = async () => {
    const cleaned = list.filter((item) => item.name && item.email);
    try {
      await updateMutation.mutateAsync({ list: cleaned });
      toast.success('担当者リストを保存しました');
    } catch (err) {
      toast.error(`保存に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-3 mb-8">
      <h3 className="text-base font-bold mb-4 pb-2 border-b border-[var(--border)]">
        担当者リスト
      </h3>
      <p className="text-xs text-[var(--text-secondary)]">
        レビュー依頼時の宛先選択に使用されます
      </p>
      {list.length === 0 && (
        <p className="text-xs text-[var(--text-muted)] italic py-2">担当者が登録されていません</p>
      )}
      {list.map((member, i) => (
        <div key={i} className="flex gap-2 items-end">
          <div className="flex-1">
            {i === 0 && <Label>名前</Label>}
            <Input
              value={member.name}
              onChange={(e) => updateRow(i, 'name', e.target.value)}
              disabled={!canEdit}
              placeholder="氏名"
            />
          </div>
          <div className="flex-1">
            {i === 0 && <Label>メールアドレス</Label>}
            <Input
              value={member.email}
              type="email"
              onChange={(e) => updateRow(i, 'email', e.target.value)}
              disabled={!canEdit}
              placeholder="name@example.com"
            />
          </div>
          {canEdit && (
            <Button type="button" variant="ghost" size="icon" onClick={() => removeRow(i)}>
              <Trash2 />
            </Button>
          )}
        </div>
      ))}
      {canEdit && (
        <div className="flex justify-between pt-2">
          <Button type="button" variant="secondary" size="sm" onClick={addRow}>
            <Plus />
            追加
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={save}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? '保存中...' : '保存'}
          </Button>
        </div>
      )}
    </div>
  );
}
